import React, { useMemo } from "react";
import { blue, green, grey, pink, red, yellow } from "@mui/material/colors";
import { lighten } from "@mui/material/styles";

/**
 * Color mapping for different order statuses
 */
const markerColors = {
  preparing: red["700"],
  packed: blue["700"],
  concluded: green["700"],
  cancelled: grey["700"],
  onWay: pink["400"],
  default: blue["700"],
};

/**
 * OrderMarker component for displaying individual markers on the map
 *
 * @param {string} idx - Order index to display on the marker
 * @param {string} status - Status of the order
 * @param {boolean} isSelectedToDispatch - Whether the order is selected for dispatch
 * @param {boolean} isHovered - Whether the marker is being hovered
 * @param {number} markerSize - Size of the marker
 * @returns {JSX.Element} Styled marker icon
 */
const OrderMarker = React.memo(
  ({
    idx,
    status,
    isSelectedToDispatch = false,
    isHovered = false,
    markerSize = 30,
  }) => {
    const iconColor = useMemo(() => {
      if (isSelectedToDispatch) return yellow["600"];
      if (isHovered) return yellow["400"];
      return markerColors[status] || markerColors.default;
    }, [status, isSelectedToDispatch, isHovered]);

    const gradientId = useMemo(
      () => `markerGradient-${idx}-${status}`,
      [idx, status]
    );

    const idxStyle = useMemo(
      () => ({
        position: "absolute",
        zIndex: 9998,
        top: idx.length > 2 ? "15%" : "8%",
        left: "4px",
        color: "#222222",
        backgroundColor: "#ffffff",
        width: "80%",
        margin: 0,
        borderRadius: "12px",
        textAlign: "center",
        fontWeight: "bold",
        fontSize: idx.length > 2 ? 14 : 16,
      }),
      [idx.length]
    );

    const markerContainerStyle = useMemo(
      () => ({
        display: "flex",
        alignItems: "center",
        position: "relative",
        filter: isHovered
          ? "drop-shadow(3px 5px 4px rgb(0 0 0 / 0.6))"
          : "drop-shadow(3px 5px 2px rgb(0 0 0 / 0.4))",
        transform: isHovered ? "scale(1.1)" : "scale(1)",
        transition: "transform 0.2s ease, filter 0.2s ease",
      }),
      [isHovered]
    );

    return (
      <div style={markerContainerStyle}>
        <svg
          width={markerSize}
          height={markerSize * 1.2}
          viewBox="0 0 384 512"
          style={{
            position: "relative",
            fontSize: markerSize,
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor={iconColor} />
              <stop offset="100%" stopColor={lighten(iconColor, 0.3)} />
            </linearGradient>
          </defs>
          {/* MapMarker SVG path from FontAwesome */}
          <path
            fill={`url(#${gradientId})`}
            d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z"
          />
        </svg>
        <p style={idxStyle}>{idx}</p>
      </div>
    );
  }
);

OrderMarker.displayName = "OrderMarker";

export default OrderMarker;
